import React from 'react';
import styled from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import Tooltip from './Tooltip';

const StyledButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${props => props.size === 'small' ? '0.5rem 1rem' : props.size === 'large' ? '1rem 2rem' : '0.75rem 1.5rem'};
  font-size: ${props => props.theme.typography.fontSize.base};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  text-decoration: none;
  min-width: ${props => props.fullWidth ? '100%' : 'auto'};
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}20;
  }
  
  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background-color: ${props.theme.colors.primary};
          color: ${props.theme.colors.white};
          border-color: ${props.theme.colors.primary};
          
          &:hover:not(:disabled) {
            background-color: ${props.theme.colors.primaryDark};
            border-color: ${props.theme.colors.primaryDark};
          }
        `;
      case 'secondary':
        return `
          background-color: ${props.theme.colors.white};
          color: ${props.theme.colors.primary};
          border-color: ${props.theme.colors.primary};
          
          &:hover:not(:disabled) {
            background-color: ${props.theme.colors.gray[50]};
          }
        `;
      case 'outline':
        return `
          background-color: transparent;
          color: ${props.theme.colors.text.primary};
          border-color: ${props.theme.colors.border.medium};
          
          &:hover:not(:disabled) {
            background-color: ${props.theme.colors.gray[50]};
            border-color: ${props.theme.colors.gray[400]};
          }
        `;
      case 'ghost':
        return `
          background-color: transparent;
          color: ${props.theme.colors.text.primary};
          border-color: transparent;
          
          &:hover:not(:disabled) {
            background-color: ${props.theme.colors.gray[100]};
          }
        `;
      case 'danger':
        return `
          background-color: ${props.theme.colors.error};
          color: ${props.theme.colors.white};
          border-color: ${props.theme.colors.error};
          
          &:hover:not(:disabled) {
            background-color: #dc2626;
            border-color: #dc2626;
          }
        `;
      case 'warning':
        return `
          background-color: ${props.theme.colors.warning};
          color: ${props.theme.colors.white};
          border-color: ${props.theme.colors.warning};
          
          &:hover:not(:disabled) {
            background-color: #d97706;
            border-color: #d97706;
          }
        `;
      default:
        return `
          background-color: ${props.theme.colors.gray[100]};
          color: ${props.theme.colors.text.primary};
          border-color: ${props.theme.colors.gray[300]};
          
          &:hover:not(:disabled) {
            background-color: ${props.theme.colors.gray[200]};
            border-color: ${props.theme.colors.gray[400]};
          }
        `;
    }
  }}
`;

const ButtonComponent = ({ 
  children, 
  variant = 'default', 
  size = 'medium', 
  fullWidth = false,
  disabled = false,
  loading = false,
  leftIcon,
  rightIcon,
  leftIconTooltip,
  rightIconTooltip,
  ...props 
}) => {
  const { theme } = useTheme();
  
  const renderIcon = (icon, tooltip) => {
    if (!icon) return null;
    if (tooltip) {
      return (
        <Tooltip text={tooltip} position="top">
          <span style={{ display: 'inline-flex', alignItems: 'center' }}>{icon}</span>
        </Tooltip>
      );
    }
    return icon;
  };
  
  return (
    <StyledButton
      theme={theme}
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <svg
          className="animate-spin -ml-1 mr-2 h-4 w-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      {leftIcon && !loading && (
        <span className="mr-2" style={{ display: 'inline-flex', alignItems: 'center' }}>
          {renderIcon(leftIcon, leftIconTooltip)}
        </span>
      )}
      {children}
      {rightIcon && !loading && (
        <span className="ml-2" style={{ display: 'inline-flex', alignItems: 'center' }}>
          {renderIcon(rightIcon, rightIconTooltip)}
        </span>
      )}
    </StyledButton>
  );
};

export default ButtonComponent;
